import Link from 'found/Link';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { graphql, usePaginationFragment } from 'react-relay';
import {
  Card, CardFooter, CardHeader, Table,
} from 'reactstrap';

import BadgeActiveTimeRange from 'src/components/Badge/BadgeActiveTimeRange';
import BadgePortfolioUserRole from 'src/components/Badge/BadgePortfolioUserRole';
import LoadMoreButton from 'src/components/LoadMoreButton';
import UUID from 'src/components/UUID';
import displayUserName from 'src/util/i18n/decorators/displayUserName';
import logger from 'src/util/logger';

const AdminPortfolioUserMembersFragment = graphql`
fragment AdminPortfolioUserMembers_portfolio on Portfolio
@refetchable(queryName: "PortfolioUserMembersPaginationQuery")
@argumentDefinitions(
  cursor: { type: "String" }
  count: { type: "Int", defaultValue: 10}
){
  uuid
  userMembers(first: $count, after: $cursor)
  @connection(key: "PortfolioUserMembers_userMembers") {
    edges {
      cursor
      node {
        uuid
        externalIdentifier
        active {
          start
          finish
        }
        user {
          uuid
          externalIdentifier
          givenName
          familyName
          email
          active {
            start
            finish
          }
        }
        role
      }
    }
    pageInfo {
      startCursor
      endCursor
      hasNextPage
      hasPreviousPage
    }
  }
}
`;

/**
 * Renders an alert component to inform the user that there is
 * no inactive property in the portfolio.
 * @returns {React.ReactElement} The alert component.
 */
export function noUsersMsg() {
  return (
    <FormattedMessage
      id="admin.admin_portfolio.admin_portfolio_show.admin_portfolio_user_members.no_user_member.message"
      defaultMessage="<strong>No user memberships found in this portfolio.</strong> Use the 'Add user' button to create a user member."
      values={{
        strong: (chunks) => <strong>{chunks}</strong>,
      }}
    />

  );
}

/**
 * @param {object} props
 * @param {object} props.portfolio
 * @returns {React.ReactElement} - AdminPortfolioUserMembers component
 */
function AdminPortfolioUserMembers(props) {
  if (!props) {
    return null;
  }
  const { portfolio } = props || {};
  const {
    data, loadNext, hasNext, isLoadingNext,
  } = usePaginationFragment(AdminPortfolioUserMembersFragment, portfolio);
  const userMembers = data.userMembers?.edges.map((edge) => edge.node);

  const intl = useIntl();

  /**
   * Loads the next set of users when the user hits the load more button.
   * @returns {void}
   */
  const loadMore = () => {
    if (!hasNext || isLoadingNext) {
      return;
    }

    loadNext(
      50,
      { onComplete: (error) => { logger.log({ error }); } },
    );
  };

  const copyLoadMore = intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_show.admin_portfolio_user_members.load_more_button.state_load_more.label', defaultMessage: 'Load more...' });
  const copyNoMore = intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_show.admin_portfolio_user_members.load_more_button.state_no_more.label', defaultMessage: 'No more portfolio user members to load' });

  return (
    <Card className="mb-4">
      <CardHeader>
        {intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_show.admin_portfolio_user_members.card_header.user.label', defaultMessage: 'User Members' })}
      </CardHeader>
      <Table responsive>
        <thead>
          <tr>
            <th scope="col" colSpan={3} className="text-center">
              {intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_show.admin_portfolio_user_members.card_body.head.portfolio_user_member.label', defaultMessage: 'PortfolioUserMember' })}
            </th>
            <th scope="col" colSpan={6} className="text-center">{intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_show.admin_portfolio_user_members.card_body.head.user.label', defaultMessage: 'User' })}</th>
          </tr>
          <tr>
            <th scope="col">
              <abbr title={intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_show.admin_portfolio_user_members.card_body.head.portfolio_user_member.identify.abbr.title', defaultMessage: 'Identify' })}>
                {intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_show.admin_portfolio_user_members.card_body.head.portfolio_user_member.id.label', defaultMessage: 'ID' })}
              </abbr>
            </th>
            <th scope="col">
              {intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_show.admin_portfolio_user_members.card_body.head.portfolio_user_member.active.label', defaultMessage: 'Active' })}
            </th>
            <th scope="col">
              {intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_show.admin_portfolio_user_members.card_body.head.portfolio_user_member.role.label', defaultMessage: 'Role' })}
            </th>
            <th scope="col">
              <abbr title={intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_show.admin_portfolio_user_members.card_body.head.portfolio_user_member.external_identifier.abbr.title', defaultMessage: 'External identifier' })}>
                {intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_show.admin_portfolio_user_members.card_body.head.portfolio_user_member.external_identifier.label', defaultMessage: 'Ext ID' })}
              </abbr>
            </th>
            <th scope="col">
              {intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_show.admin_portfolio_user_members.card_body.head.portfolio_user_member.username.label', defaultMessage: 'Username' })}
            </th>
            <th scope="col">
              {intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_show.admin_portfolio_user_members.card_body.head.portfolio_user_member.email.label', defaultMessage: 'Email' })}
            </th>
            <th scope="col">
              {intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_show.admin_portfolio_user_members.card_body.head.user.active.label', defaultMessage: 'Active' })}
            </th>
            <th scope="col">
              <abbr title={intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_show.admin_portfolio_user_members.card_body.head.user.identifier.abbr.title', defaultMessage: 'Identifier' })}>
                {intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_show.admin_portfolio_user_members.card_body.head.user.id.label', defaultMessage: 'ID' })}
              </abbr>
            </th>
            <th scope="col">
              <abbr title={intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_show.admin_portfolio_user_members.card_body.head.user.external_identifier.abbr.title', defaultMessage: 'External identifier' })}>
                {intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_show.admin_portfolio_user_members.card_body.head.user.external_identifier.label', defaultMessage: 'Ext ID' })}
              </abbr>
            </th>
          </tr>
        </thead>
        <tbody>
          {userMembers.length === 0 && (
            <tr>
              <td colSpan={9}>
                {noUsersMsg()}
              </td>
            </tr>
          )}
          {userMembers.map((member) => {
            const {
              active, externalIdentifier,
              uuid, role, user,
            } = member || {};
            const {
              uuid: userId, externalIdentifier: userExternalIdentifier, email: userEmail,
              active: userActive,
            } = user || {};

            return (
              <tr key={uuid}>
                <th className="fw-normal" scope="row">
                  <UUID uuid={uuid} />
                </th>
                <td aria-label="badge active">
                  <BadgeActiveTimeRange active={active} />
                </td>
                <th scope="row" colSpan={2}>
                  <BadgePortfolioUserRole userRole={role} />
                  <br />
                  {externalIdentifier}
                </th>
                <th scope="row" colSpan={2}>
                  <Link to={`/admin/portfolio-user-members/${uuid}`}>
                    <span style={{ fontWeight: 'bold' }}>{displayUserName(intl, user)}</span>
                  </Link>
                  <br />
                  {userEmail}
                  <br />
                </th>
                <td aria-label="badge active">
                  <BadgeActiveTimeRange active={userActive} />
                </td>
                <th className="fw-normal" scope="row" colSpan={2}>
                  <UUID uuid={userId} />
                  {userExternalIdentifier}
                </th>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <CardFooter>
        <LoadMoreButton
          copyLoadMore={copyLoadMore}
          copyNoMore={copyNoMore}
          hasNext={hasNext}
          onClick={loadMore}
        />
      </CardFooter>
    </Card>
  );
}

AdminPortfolioUserMembers.propTypes = {
  // note: known bug https://github.com/jsx-eslint/eslint-plugin-react/issues/1002#issuecomment-320160686
  // eslint-disable-next-line react/forbid-prop-types, react/no-unused-prop-types
  portfolio: PropTypes.object.isRequired,
};

export default AdminPortfolioUserMembers;
